/**
 * article-detail.js — 文章詳情頁互動（純前端 prototype）
 *
 * 職責：
 *   1. 由資料渲染評論列表（單一真相 = data/article-detail.js）
 *   2. 評價表單星等選擇（點擊 / 鍵盤；同步 hidden input 值與 aria）
 *   3. 表單送出 → 重用 modal 元件顯示「評價已送出，待審核」回饋（不真送後端）
 *
 * 載入方式：本頁 HTML 以獨立 <script type="module"> 引入（不進 main.js）。
 * modal primitive 仍由 main.js 全域載入並 auto-init；此處只用其程式式 API。
 *
 * 清理：所有 listener 在 pagehide（bfcache 友善）時解除，避免重複綁定 / 記憶體滯留。
 */

import { Modal } from '../components/modal.js';
import { initFavToggle } from '../components/fav-toggle.js';
import { REVIEWS, REVIEW_SUMMARY } from '../data/article-detail.js';
import { escapeHtml as esc } from '../utils/dom.js';

// 桌機固定高可捲容器斷點：與評價兩欄 / article-detail.css 的 1024 斷點同源（單一真相）。
// ≥1024 評論列表是固定高捲動容器，IntersectionObserver(root=列表本身)捲近底自動載入；
// <1024 自然流、無 viewport observer，初顯 MOBILE_INITIAL 則 + 「載入更多」按鈕點擊才 append。
// 兩斷點兩套載入機制：桌機 = 容器內無限捲動、手機 = 按鈕分批（解 footer-trap：observer 不再無限撐頁）。
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');

// 桌機每批筆數（容器 observer 初批 + 後續批；手機按鈕每點一批亦沿用此值）。
// 評論少（≤ 門檻）時一次全顯、零載入 UI（graceful degradation）：桌機門檻 BATCH、手機門檻 MOBILE_INITIAL。
const REVIEW_BATCH = 10;
// 手機首屏顯示筆數（USER 決策：3 則 + 「載入更多」）。≤ 此數時全顯、無按鈕、無結尾提示。
const MOBILE_INITIAL = 3;
// 哨兵預載邊距：距離容器底 200px 即觸發下一批（桌機容器 observer 用；手機不建 observer）。
const REVIEW_SENTINEL_MARGIN = '200px';

const STAR_FULL = 'M10 1.5l2.472 5.008 5.528.803-4 3.898.944 5.505L10 14.115l-4.944 2.602.944-5.505-4-3.898 5.528-.803z';

/** 產生 5 顆星（亮起數量 = 四捨五入後的 rating）的 inline SVG 群組字串（裝飾用，aria-hidden）。 */
function renderStars(rating) {
  let out = '';
  for (let i = 1; i <= 5; i += 1) {
    const on = i <= Math.round(rating);
    out += `<svg class="article-review__star-icon${on ? ' is-on' : ''}" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true"><path d="${STAR_FULL}" /></svg>`;
  }
  return out;
}

/**
 * 單則評論卡 markup（頭像首字 + 名字 + 星 + 內容 + 日期）。
 * body 預設套 --clamp（截斷 4 行）；長文展開鈕 hidden，由 measureVisibleClamps() 量測後才顯。
 * status 寫進 data-status（資訊性；公開過濾已於 render 前用 publishedReviews() 完成，DOM 內只有 published）。
 */
function reviewItemMarkup(r) {
  const initial = esc(r.name.trim().charAt(0));
  return `
      <li class="article-review__item" data-status="${esc(r.status)}">
        <div class="article-review__avatar" aria-hidden="true">${initial}</div>
        <div class="article-review__content">
          <div class="article-review__item-head">
            <p class="article-review__author">${esc(r.name)}</p>
            <div class="article-review__stars" role="img" aria-label="評分 ${r.rating} 顆星，滿分 5 顆星">${renderStars(r.rating)}</div>
          </div>
          <p class="article-review__body article-review__body--clamp" data-review-body>${esc(r.body)}</p>
          <button class="article-review__toggle" type="button" data-review-toggle aria-expanded="false" hidden>展開全文</button>
          <time class="article-review__date" datetime="${esc(r.date)}">${esc(r.dateLabel)}</time>
        </div>
      </li>`;
}

/**
 * 量測評論內文是否被 clamp 截斷，決定展開鈕顯隱。
 *   截斷（scrollHeight > clientHeight）→ 顯示 toggle；
 *   未截斷 → 移除 --clamp（避免無謂的 -webkit-box 影響）並維持 toggle hidden。
 *
 * 無限捲動下 append 的卡片即刻可見（不帶 hidden）→ 直接全量；冪等 flag 確保每張只量一次。
 *   故每批 append 後對「新加入的卡」補量即可（已量過的跳過）。
 * 冪等：量過的標 data-measured，已展開（is-expanded）的不重置，避免重量時還原使用者展開狀態。
 * 須在 fonts.ready 後量（字體未載入時行高/換行不準 → 誤判截斷數）。
 */
function measureVisibleClamps(listEl) {
  listEl.querySelectorAll('.article-review__item [data-review-body]').forEach((body) => {
    if (body.dataset.measured === '1') return; // 量過不重量（已 append 的卡再觸發時跳過）
    const toggle = body.nextElementSibling; // [data-review-toggle]
    const clamped = body.scrollHeight > body.clientHeight + 1; // +1 容差，避免次像素誤判
    if (clamped) {
      if (toggle) toggle.hidden = false;
    } else {
      body.classList.remove('article-review__body--clamp');
      if (toggle) toggle.hidden = true;
    }
    body.dataset.measured = '1';
  });
}

/**
 * 渲染評論列表 + 長文展開。兩斷點兩套載入機制（解 footer-trap）：
 *   - 只取 published 評論（status==='published'），DOM 內不含 pending / returned；
 *   - 桌機 ≥1024：固定高捲動容器 + IntersectionObserver（root=列表本身）。初批 REVIEW_BATCH(10)，
 *       哨兵捲近底（rootMargin 預載）即 append 下一批；全載入後 disconnect。行為與 dc72bfe/84ce3fc 一致；
 *   - 手機 <1024：自然流、**不建 viewport observer**（避免捲到頁底自動 append 把相關專欄 + footer 無限
 *       往下推＝footer-trap）。初批 MOBILE_INITIAL(3)，下方「載入更多評價（剩 X 則）」按鈕點擊才 append 一批；
 *   - 斷點切換（matchMedia change）：跨進 ≥1024 → 隱按鈕 + 建容器 observer；跨出 <1024 → disconnect observer +
 *       顯按鈕（仍有未載入時）。兩邊都不重複載入既有卡（rendered 為單一真相）；
 *   - 少評論（桌機 ≤ BATCH / 手機 ≤ MOBILE_INITIAL）：初批即全部 → 無 observer / 無按鈕 / 無結尾（graceful）；
 *   - 載入中：append 批次期間 region 設 aria-busy、顯示 loading affordance（桌機 [data-review-loading]，
 *       手機 按鈕內 spinner + 文字「載入中…」；reduced-motion 下 spinner 不轉、僅文字）；
 *   - 全載入且總數 > 該斷點門檻：顯示 [data-review-end]「已顯示全部 N 則」（手機同時移除按鈕）；
 *   - body 套 --clamp，每批 append 後量測決定展開鈕；點鈕切 .is-expanded + 文字 + aria。
 * 空資料顯示空狀態。observer / listener / matchMedia 全入 cleanups。
 * 註：?rpage 參數已失效（分批載入取代分頁）；保留於 URL 不讀取、不致錯。
 */
function renderReviewList(scope, cleanups) {
  const listEl = scope.querySelector('[data-review-list]');
  if (!listEl) return;
  const emptyEl = scope.querySelector('[data-review-empty]');
  const sentinelEl = listEl.querySelector('[data-review-sentinel]');
  const loadingEl = listEl.querySelector('[data-review-loading]');
  const endEl = listEl.querySelector('[data-review-end]');
  // 「載入更多」按鈕（手機用；桌機維持 hidden）。位於列表與相關專欄之間 → footer 恆可達。
  const moreBtn = scope.querySelector('[data-review-more]');
  const moreLabel = moreBtn && moreBtn.querySelector('[data-review-more-label]');
  const moreSpinner = moreBtn && moreBtn.querySelector('[data-review-more-spinner]');

  // 公開過濾：列表只放 published（pending / returned 不公開，不進 DOM）。
  const published = REVIEWS.filter((r) => r.status === 'published');

  // 空狀態：無公開評價 → 顯示空訊息，隱藏哨兵 / 載入 / 結尾列 / 按鈕。
  if (!published.length) {
    if (emptyEl) emptyEl.hidden = false;
    if (sentinelEl) sentinelEl.hidden = true;
    if (loadingEl) loadingEl.hidden = true;
    if (endEl) endEl.hidden = true;
    if (moreBtn) moreBtn.hidden = true;
    return;
  }
  if (emptyEl) emptyEl.hidden = true;

  const total = published.length;
  // 每斷點的「初顯筆數」與「是否多於初顯」門檻（少評論 graceful degradation 用）：
  //   桌機初批 REVIEW_BATCH(10)、手機初批 MOBILE_INITIAL(3)。
  const initialCount = () => (DESKTOP_MQ.matches ? REVIEW_BATCH : MOBILE_INITIAL);
  const hasMany = () => total > initialCount(); // 是否多於初顯（決定是否需載入 UI / 結尾 / 按鈕）
  let rendered = 0; // 已 append 筆數（兩斷點共用 → 切換時不重複載入）

  // append 一批：批次插入（單一 fragment，不在 loop 內逐筆 append DOM）；插在哨兵之前，哨兵恆置尾。
  //   count 預設 REVIEW_BATCH；初批可指定 MOBILE_INITIAL。measure=true 即刻量 clamp；
  //   初批 measure=false，量測延到 fonts.ready（字體載入前行高/換行不準，data-measured 冪等過早量會鎖死）。
  // 更新 rendered → 全載入則顯結尾提示。回傳是否仍有未載入評論。
  // TODO(backend): appendBatch 改 await fetch GET reviews（cursor 分頁 + summary），移除 buildMockReviews — 見 docs/BACKEND_HANDOFF.md#article-reviews-list
  const appendBatch = (measure, count) => {
    const n = count || REVIEW_BATCH;
    const slice = published.slice(rendered, rendered + n);
    if (slice.length) {
      const frag = document.createElement('template');
      frag.innerHTML = slice.map(reviewItemMarkup).join('');
      listEl.insertBefore(frag.content, sentinelEl);
      rendered += slice.length;
      if (measure) measureVisibleClamps(listEl);
    }
    const remaining = rendered < total;
    if (!remaining && hasMany() && endEl) {
      // 全載入且本就多於初顯 → 顯示「已顯示全部 N 則」（少評論時 hasMany=false，永不顯）。
      endEl.textContent = `已顯示全部 ${total} 則評價`;
      endEl.hidden = false;
    }
    return remaining;
  };

  // ===== 桌機：IntersectionObserver 容器內無限捲動（root = 列表本身）=====
  // 僅 ≥1024 存在；hasMany=false 時不建（少評論 graceful）。
  let observer = null;
  const disconnectObserver = () => {
    if (observer) { observer.disconnect(); observer = null; }
  };

  // 桌機自動載下一批（observer 觸發）。
  const loadNextDesktop = () => {
    if (loadingEl) loadingEl.hidden = false;
    listEl.setAttribute('aria-busy', 'true');
    // 同步 append（prototype 本地資料；接 API 後改 await fetch，aria-busy / loading 範式不變）。
    const remaining = appendBatch(true);
    if (loadingEl) loadingEl.hidden = true;
    listEl.setAttribute('aria-busy', 'false');
    if (!remaining) disconnectObserver(); // 全載入 → 停止觀察
  };

  const buildObserver = () => {
    // 桌機才建；少評論 / 無哨兵 / 已全載入 → 不建。root 恆為列表容器（桌機才呼叫此函式）。
    if (!DESKTOP_MQ.matches || !hasMany() || !sentinelEl || rendered >= total) return;
    disconnectObserver();
    observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && rendered < total) loadNextDesktop();
    }, {
      root: listEl, // 桌機列表本身是 overflow:auto 容器
      rootMargin: REVIEW_SENTINEL_MARGIN,
    });
    observer.observe(sentinelEl);
  };

  // ===== 手機：「載入更多」按鈕分批（無 observer）=====
  // 更新按鈕文字 / 顯隱：仍有未載入 → 顯按鈕「載入更多評價（剩 X 則）」；全載入 → 隱按鈕（結尾由 appendBatch 顯）。
  const syncMoreButton = () => {
    if (!moreBtn) return;
    const remaining = total - rendered;
    if (remaining > 0 && hasMany()) {
      if (moreLabel) moreLabel.textContent = `載入更多評價（剩 ${remaining} 則）`;
      moreBtn.hidden = false;
    } else {
      moreBtn.hidden = true; // 全載入或少評論 → 不顯（結尾提示已由 appendBatch 處理）
    }
  };

  // 手機點擊「載入更多」：append 一批。append 為同步（本地資料）→ loading 態可能瞬時，仍正確 wire。
  const onMoreClick = () => {
    if (rendered >= total) return;
    // 載入態：按鈕 aria-busy + 露出 spinner + 文字「載入中…」；列表 region aria-busy。
    moreBtn.setAttribute('aria-busy', 'true');
    if (moreSpinner) moreSpinner.hidden = false;
    if (moreLabel) moreLabel.textContent = '載入中…';
    listEl.setAttribute('aria-busy', 'true');

    const remaining = appendBatch(true); // 後續批 fonts 已載入 → 立即量 clamp

    listEl.setAttribute('aria-busy', 'false');
    moreBtn.setAttribute('aria-busy', 'false');
    if (moreSpinner) moreSpinner.hidden = true;

    if (remaining) {
      syncMoreButton(); // 仍有未載入 → 更新「剩 X 則」、焦點保持在按鈕（不掉到 body）
    } else {
      // 全載入：移除按鈕，焦點移到結尾提示（替代按鈕的可聚焦目標，不掉到 body）。
      moreBtn.hidden = true;
      if (endEl && !endEl.hidden) {
        endEl.setAttribute('tabindex', '-1');
        endEl.focus();
      }
    }
  };
  if (moreBtn) {
    moreBtn.addEventListener('click', onMoreClick);
    cleanups.push(() => moreBtn.removeEventListener('click', onMoreClick));
  }

  // ===== 初始渲染（依當前斷點決定初批筆數）=====
  // 哨兵僅桌機 observer 用 → 手機隱藏（inert）。
  if (sentinelEl) sentinelEl.hidden = !DESKTOP_MQ.matches;
  appendBatch(false, initialCount());
  const runInitialMeasure = () => measureVisibleClamps(listEl);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(runInitialMeasure);
  } else {
    runInitialMeasure();
  }

  // 依斷點啟動對應載入機制（仍有未載入時）：桌機建 observer、手機顯按鈕。
  if (DESKTOP_MQ.matches) buildObserver();
  else syncMoreButton();

  // ===== 斷點切換：重建正確機制 =====
  //   跨進 ≥1024 → 隱哨兵→顯 + 隱按鈕 + 建容器 observer；跨出 <1024 → disconnect observer + 顯哨兵→隱 + 顯按鈕。
  //   rendered 為單一真相（兩斷點共用）→ 切換不重複載入既有卡。
  const onDesktopMqChange = () => {
    if (DESKTOP_MQ.matches) {
      // → 桌機：關按鈕載入態、隱按鈕；哨兵恢復供 observer 用；建 observer（仍有未載入時）。
      if (moreBtn) {
        moreBtn.hidden = true;
        moreBtn.setAttribute('aria-busy', 'false');
        if (moreSpinner) moreSpinner.hidden = true;
      }
      if (sentinelEl) sentinelEl.hidden = false;
      buildObserver();
    } else {
      // → 手機：拆 observer（避免 viewport / 容器 root 混用）+ 隱哨兵 + 隱桌機 loading 列；顯按鈕（仍有未載入時）。
      disconnectObserver();
      if (sentinelEl) sentinelEl.hidden = true;
      if (loadingEl) loadingEl.hidden = true;
      listEl.setAttribute('aria-busy', 'false');
      syncMoreButton();
    }
  };
  if (DESKTOP_MQ.addEventListener) DESKTOP_MQ.addEventListener('change', onDesktopMqChange);
  else if (DESKTOP_MQ.addListener) DESKTOP_MQ.addListener(onDesktopMqChange);
  cleanups.push(() => {
    if (DESKTOP_MQ.removeEventListener) DESKTOP_MQ.removeEventListener('change', onDesktopMqChange);
    else if (DESKTOP_MQ.removeListener) DESKTOP_MQ.removeListener(onDesktopMqChange);
    disconnectObserver();
  });

  const onToggleClick = (e) => {
    const btn = e.target.closest('[data-review-toggle]');
    if (!btn || !listEl.contains(btn)) return;
    const body = btn.previousElementSibling; // [data-review-body]
    if (!body) return;
    const expanded = body.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    btn.textContent = expanded ? '收合' : '展開全文';
  };
  listEl.addEventListener('click', onToggleClick);
  cleanups.push(() => listEl.removeEventListener('click', onToggleClick));
}

/** 更新總星評摘要（平均星 + 顆數）。 */
function renderSummary(scope) {
  const starsEl = scope.querySelector('[data-review-summary-stars]');
  const avgEl = scope.querySelector('[data-review-summary-average]');
  const countEl = scope.querySelector('[data-review-summary-count]');
  if (starsEl) {
    starsEl.innerHTML = renderStars(REVIEW_SUMMARY.average);
    starsEl.setAttribute('aria-label', `平均評分 ${REVIEW_SUMMARY.average} 顆星，滿分 5 顆星`);
  }
  if (avgEl) avgEl.textContent = REVIEW_SUMMARY.average.toFixed(1);
  if (countEl) countEl.textContent = String(REVIEW_SUMMARY.count);
}

function initRatingInput(form, cleanups) {
  const radioWrap = form.querySelector('[data-rating-input]');
  if (!radioWrap) return;
  const buttons = Array.from(radioWrap.querySelectorAll('.article-review__rate-star'));
  const hidden = form.querySelector('#review-rating');
  const label = form.querySelector('[data-rating-label]');

  /** 只切換「鎖定亮星」class（不動 aria / hidden / tabindex），供 paint 與 hover 還原共用。 */
  const lightUp = (value) => {
    buttons.forEach((btn) => {
      btn.classList.toggle('is-on', Number(btn.dataset.value) <= value);
    });
  };

  /** hover 預覽用：1~N 連亮預覽色（is-preview，比鎖定色亮一階）；value=0 清除全部預覽。 */
  const previewUp = (value) => {
    buttons.forEach((btn) => {
      btn.classList.toggle('is-preview', value > 0 && Number(btn.dataset.value) <= value);
    });
  };

  /** 更新文字回饋：已選顯示「已選 N 顆星」，未評顯示「點擊評分」（淡色）。 */
  const setLabel = (value) => {
    if (!label) return;
    label.textContent = value ? `已選 ${value} 顆星` : '點擊評分';
    label.classList.toggle('article-review__rate-result--empty', !value);
  };

  /** 鎖定狀態（click / 鍵盤）：亮星 + aria + tabindex + hidden 值 + 文字。 */
  const paint = (value) => {
    lightUp(value);
    buttons.forEach((btn) => {
      const v = Number(btn.dataset.value);
      btn.setAttribute('aria-checked', v === value ? 'true' : 'false');
      btn.tabIndex = v === value || (value === 0 && v === 1) ? 0 : -1;
    });
    if (hidden) hidden.value = value ? String(value) : '';
    setLabel(value);
  };

  const lockedValue = () => Number(hidden && hidden.value) || 0;

  /** hover 暫態：1~N 連亮預覽色（is-preview 疊在鎖定 is-on 之上），文字同步預覽值。 */
  const onPointerOver = (e) => {
    const btn = e.target.closest('.article-review__rate-star');
    if (!btn || btn.disabled) return;
    const v = Number(btn.dataset.value);
    previewUp(v);
    setLabel(v);
  };

  /** 離開星列：清除預覽，文字還原當前鎖定值。 */
  const onPointerLeave = () => {
    previewUp(0);
    setLabel(lockedValue());
  };

  const onClick = (e) => {
    const btn = e.target.closest('.article-review__rate-star');
    if (!btn) return;
    paint(Number(btn.dataset.value));
  };

  const onKeydown = (e) => {
    const current = Number(hidden && hidden.value) || 0;
    let next = current;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(5, current + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(1, current - 1);
    else return;
    e.preventDefault();
    paint(next);
    const target = buttons.find((b) => Number(b.dataset.value) === next);
    if (target) target.focus();
  };

  radioWrap.addEventListener('click', onClick);
  radioWrap.addEventListener('keydown', onKeydown);
  radioWrap.addEventListener('mouseover', onPointerOver);
  radioWrap.addEventListener('mouseleave', onPointerLeave);
  cleanups.push(() => {
    radioWrap.removeEventListener('click', onClick);
    radioWrap.removeEventListener('keydown', onKeydown);
    radioWrap.removeEventListener('mouseover', onPointerOver);
    radioWrap.removeEventListener('mouseleave', onPointerLeave);
  });

  paint(0);

  // 提供給送出成功後重置星等 + 文字回饋（避免在表單流程重複實作 DOM 清除）
  return { reset: () => { previewUp(0); paint(0); } };
}

function initReviewForm(form, cleanups, rating) {
  const hidden = form.querySelector('#review-rating');
  const ratingError = form.querySelector('[data-rating-error]');

  // TODO(backend): 評價送出改打 API（rating/name/body + 審核流程），resolve 才開 modal — 見 docs/BACKEND_HANDOFF.md#article-review-submit
  const onSubmit = (e) => {
    e.preventDefault();
    const value = Number(hidden && hidden.value) || 0;
    if (value < 1) {
      if (ratingError) ratingError.hidden = false;
      const firstStar = form.querySelector('.article-review__rate-star');
      if (firstStar) firstStar.focus();
      return;
    }
    if (ratingError) ratingError.hidden = true;

    Modal.open('#review-sent-modal');
    form.reset();
    // 星等 + 文字回饋一併歸零（單一真相：rating controller）
    if (rating) rating.reset();
  };

  form.addEventListener('submit', onSubmit);
  cleanups.push(() => form.removeEventListener('submit', onSubmit));
}

/**
 * 收藏按鈕互動（#2，前台自包，不依賴後台）— 改用共用 fav-toggle 模組。
 *   未收藏：空心書籤 / 文字「收藏文章」/ .button--primary navy / aria-pressed false
 *   已收藏：實心書籤（.is-filled）/ 文字「已收藏」/ .button--secondary teal / aria-pressed true
 * false→true 時 icon 彈一下（.is-popping）。狀態存 localStorage（key medproof:fav:articles）。
 * 行為與原自包版一致；邏輯單一真相在 components/fav-toggle.js。
 */
function initFavorite(cleanups) {
  const btn = document.querySelector('[data-fav-toggle]');
  if (!btn) return;
  initFavToggle(
    {
      btn,
      id: btn.dataset.articleId,
      storageKey: 'medproof:fav:articles',
      restLabel: '收藏文章',
      activeLabel: '已收藏',
      iconSelector: '.article-detail-hero__fav-icon',
      restClass: 'button--primary',
      activeClass: 'button--secondary',
    },
    cleanups,
  );
}

function init() {
  const cleanups = [];

  // 收藏按鈕在 hero（.article-review 之外）→ 獨立 init，不受評價區存在與否影響
  initFavorite(cleanups);

  const scope = document.querySelector('.article-review');
  if (scope) {
    renderReviewList(scope, cleanups);
    renderSummary(scope);

    const form = scope.querySelector('[data-review-form]');
    if (form) {
      const rating = initRatingInput(form, cleanups);
      initReviewForm(form, cleanups, rating);
    }
  }

  if (!cleanups.length) return;

  const onPageHide = () => {
    cleanups.forEach((fn) => fn());
    window.removeEventListener('pagehide', onPageHide);
  };
  window.addEventListener('pagehide', onPageHide);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
